import React, { useImperativeHandle, useRef } from 'react';
import { Card, Checkbox, Divider, Form } from 'antd';
import ImageCropUpload from './ImageCropUpload';
import { TagSelector } from '../../../components/formItems';
import useCreateImageThumbnail from '../../gql/hooks/useCreateImageThumbnail';
import './styles.less';
import { useWatch } from 'antd/es/form/Form';
import { ColoredTag } from './GenericElements';

/**
 * Card every generic form item sits in. `extra` and `tip` are whatever the tab decides to inject -
 * the divider only appears when there is a tip to divide from.
 */
const FormItemCard = ( { title, extra, tip, className, style, children } ) => {

    return <Card title={ title } extra={ extra } className={ className } style={ style }>
        { children }
        { tip && <>
            <Divider />
            <p className='tip'>{ tip }</p>
        </> }
    </Card>;
};

/** The cropper writes a data url into the form. Anything else is already hosted, or empty. */
function needsUpload( value ) {
    return typeof value === 'string' && value.startsWith( 'data:' );
}

/**
 * Image card that defers the actual upload until save.
 *
 * The cropper only writes a data url into the form, so nothing is uploaded for an image the user
 * never saves. The upload happens when the page's `beforeSave` awaits the imperative `onSubmit`,
 * which returns the url to put on the mutation input:
 *
 *     const imageRef = useRef();
 *     async function beforeSave( { input } ) {
 *         return { ...input, image: await imageRef.current?.onSubmit() };
 *     }
 *
 * `onSubmit` is safe to call unconditionally: it uploads only when the value is a data url, and
 * otherwise hands back whatever is already there (the hosted url, or null once removed). It throws
 * when the upload fails.
 *
 * @param {Object} ref exposes { onSubmit } resolving to the image url
 * @param {Form} form form instance, needed to read and write the value
 * @param {string!} name form field holding the image url. Defaults to 'image'
 * @param {string!} title card title, also the human name used in the crop modal and messages
 * @param {node!} extra rendered in the card's extra slot
 * @param {node!} tip italic help text under a divider
 */
export const ThumbnailFormItem = ( {
    ref, form,
    name = 'image',
    title = 'Image',
    extra,
    tip,
    required = false,
    requiredMessage,
    cropTitle,
    uploadText = 'Upload',
    aspect = 1,
    cropHeight = 400,
    pngOnly = false,
    className = 'image-card',
    style = { maxWidth: 400 },
    disabled,
    onDirty,
    onValidate
} ) => {

    const { createImageThumbnail } = useCreateImageThumbnail();

    // the File behind the current data url, kept in a ref because nothing renders from it
    const uploadFile = useRef( null );

    useImperativeHandle( ref, () => ( {
        onSubmit
    } ) );

    async function onSubmit() {
        const value = form.getFieldValue( name );

        if ( !needsUpload( value ) ) {
            return value ?? null;
        }

        // a data url with no file behind it is not something we can upload, so leave it be
        if ( !uploadFile.current ) {
            return value;
        }

        const image = await createImageThumbnail( uploadFile.current );

        if ( !image ) {
            throw new Error( `Could not save ${ title.toLowerCase() }` );
        }

        form.setFieldValue( name, image );
        return image;
    }

    return <FormItemCard title={ title } extra={ extra } tip={ tip } className={ className } style={ style }>
        <Form.Item
            name={ name }
            // GenericTab reads severity off the word "required" in the message, so keep it in there
            rules={ required ? [ { required: true, message: requiredMessage ?? `${ title } is required` } ] : undefined }
        >
            <ImageCropUpload
                disabled={ disabled }
                name={ title }
                title={ cropTitle ?? `Crop ${ title }` }
                uploadText={ uploadText }
                aspect={ aspect }
                cropHeight={ cropHeight }
                pngOnly={ pngOnly }
                onFileChange={ file => {
                    uploadFile.current = file;
                    onDirty?.();
                    onValidate?.();
                } }
            />
        </Form.Item>
    </FormItemCard>;
};

/**
 * Featured toggle card.
 * @param {string!} name form field holding the flag. Defaults to 'isFeatured'
 * @param {string!} label checkbox label
 * @param {node!} extra rendered in the card's extra slot, eg a tag reflecting the current value
 * @param {node!} tip italic help text under a divider explaining what featuring does
 */
export const FeaturedFormItem = ( {
    name = 'isFeatured', title = 'Featured', label = 'Featured Content',
    extra: _extra, tip = 'Featured content appears before non-featured content',
    form,
    className,
    style = { maxWidth: 300 },
    disabled,
    onDirty
} ) => {

    const isFeatured = useWatch( name );

    const extra = _extra ?? <ColoredTag.Featured isFeatured={ isFeatured } />;

    return <FormItemCard title={ title } extra={ extra } tip={ tip } className={ className } style={ style }>
        <Form.Item name={ name } valuePropName='checked' style={ { margin: 0 } }>
            <Checkbox disabled={ disabled } onChange={ () => onDirty?.() }>{ label }</Checkbox>
        </Form.Item>
    </FormItemCard>;
};

/**
 * Tag picker card. The value is a list of tag ids, which is not part of any patch input - write it
 * with syncObjectRelationships from the page's `beforeSave`:
 *
 *     await syncRelationships( { variables: { input: {
 *         objectType: 'resource', objectId: id,
 *         relatedResourceType: 'tags',
 *         relatedResourceIds: form.getFieldValue( 'tagIds' ) ?? []
 *     } } } );
 *
 * @param {string!} name form field holding the tag ids. Defaults to 'tagIds'
 * @param {function} onDirty called on every change, for GenericTab's makeDirty
 */
export const TagsFormItem = ( {
    name = 'tagIds',
    title = 'Tags',
    extra,
    tip,
    className,
    style,
    disabled,
    onDirty
} ) => {

    return <FormItemCard title={ title } extra={ extra } tip={ tip } className={ className } style={ style }>
        <Form.Item name={ name } valuePropName='targetKeys'>
            <TagSelector disabled={ disabled } onChange={ () => onDirty?.() } />
        </Form.Item>
    </FormItemCard>;
};
